/**
 * 固有名詞の行入力（用語・読み仮名）の変換規則（v3.7.1 Unit 005）。
 *
 * 送信形式は従来どおり `表記` / `表記:よみ` の改行連結（backend の parse_session_terms が
 * 行ごとに最初のコロンで分ける）。読みにコロンや改行が残ると区切りと誤解されるため取り除く。
 */

export interface TermRow {
  term: string
  reading: string
}

/** 画面上の行。id は React key とフォーカス先の特定にだけ使い、送信には含めない。 */
export interface TermRowItem extends TermRow {
  id: number
}

let nextId = 0

export function createTermRowItem(row: TermRow = { term: '', reading: '' }): TermRowItem {
  nextId += 1
  return { ...row, id: nextId }
}

const COLON = /[:：]/
const COLONS = /[:：]/g
// backend の str.splitlines() が行区切りとみなす文字（U+2028 等も含む）。用語・読みに残すと語が割れる
const UNICODE_LINE_SEPARATORS = String.fromCharCode(0x2028, 0x2029)
export const LINE_BREAK = new RegExp(String.raw`\r\n|[\n\v\f\r\x1c-\x1e\x85${UNICODE_LINE_SEPARATORS}]`)
const LINE_BREAKS = new RegExp(LINE_BREAK.source, 'g')

/**
 * 用語の最初のコロン（半角・全角）より前を用語、後ろを読みにする（読みは置き換え）。
 * 後ろが空白だけならコロンを取り除くだけで読みは変えない。コロンがなければそのまま返す。
 */
export function splitTermRow<T extends TermRow>(row: T): T {
  const match = COLON.exec(row.term)
  if (!match) return row
  const term = row.term.slice(0, match.index)
  const after = row.term.slice(match.index + 1)
  return after.trim() ? { ...row, term, reading: after } : { ...row, term }
}

/** 複数行テキストを行に分け、空白だけの行を捨て、各行をコロン分割する。 */
export function parseTermLines(text: string): TermRow[] {
  return text
    .split(LINE_BREAK)
    .filter((line) => line.trim())
    .map((line) => splitTermRow({ term: line, reading: '' }))
}

export function isBlankRow(row: TermRow): boolean {
  return !row.term.trim() && !row.reading.trim()
}

/** 送信用の `terms` 文字列。用語が1つもなければ undefined（API に送らない）。 */
export function buildTermsString(rows: TermRow[]): string | undefined {
  const lines = rows
    .map((row) =>
      splitTermRow({ term: row.term.replace(LINE_BREAKS, ''), reading: row.reading.replace(LINE_BREAKS, '') }),
    )
    .map((row) => ({ term: row.term.trim(), reading: row.reading.replace(COLONS, '').trim() }))
    .filter((row) => row.term)
    .map((row) => (row.reading ? `${row.term}:${row.reading}` : row.term))
  return lines.length ? lines.join('\n') : undefined
}
